import { useIsMutating, useQueryClient } from '@tanstack/react-query';

import type {
  AdminIdentity,
  AdminMfaUpdateBody,
  AdminPlan,
  CreateAdminBody,
  InvitedAdmin,
  ModerationDecisionBody,
  ModerationDecisionResult,
  SupportTicket,
} from '@/contracts';
import { adminApi, consoleApi } from './api';
import { reportCsvFilename, saveFile } from './download';
import { queryKeys, type ModerationSubject } from './query-keys';
import { useAdminMutation, type MutationFeedback } from './use-admin-mutation';

/**
 * The console's writes, one hook each: the call, and the caches it makes stale. The
 * screen brings the words — the toasts — and what it does next; the data layer owns
 * what the write touches, so a screen cannot forget the shell's badges or the inbox.
 *
 * The companion of `queries.ts`: between them, the only places outside `src/app` that
 * name an endpoint.
 */

/** A decision on one item; the queue's subject comes from the hook. A rejection needs a reason. */
export type ModerationDecisionInput = { itemId: string } & Omit<ModerationDecisionBody, 'subject'>;

/** Approve or reject a queued item; the queue and the shell's badges both move. */
export const useModerationDecisionMutation = (
  subject: ModerationSubject,
  feedback: MutationFeedback<ModerationDecisionResult, ModerationDecisionInput>,
) =>
  useAdminMutation({
    fn: ({ itemId, decision, reason }: ModerationDecisionInput) =>
      consoleApi.decideModeration(itemId, {
        subject,
        decision,
        ...(reason ? { reason } : {}),
      }),
    invalidate: [queryKeys.moderation.queue(subject), queryKeys.pendingCounts()],
    ...feedback,
  });

interface TalentReason {
  id: string;
  reason: string;
}

/** The list and every talent detail sit under one key; a delete stales both. */
export const useDeleteTalentMutation = (feedback: MutationFeedback<void, TalentReason>) =>
  useAdminMutation({
    fn: ({ id, reason }: TalentReason) => consoleApi.deleteTalent(id, reason),
    invalidate: [queryKeys.talents.all()],
    ...feedback,
  });

export const useRemoveTalentPhotoMutation = (feedback: MutationFeedback<void, TalentReason>) =>
  useAdminMutation({
    fn: ({ id, reason }: TalentReason) => consoleApi.removeTalentPhoto(id, reason),
    invalidate: [queryKeys.talents.all()],
    ...feedback,
  });

/**
 * A ticket write lands the same way whatever it was: the ticket the API answered with
 * replaces the cached one — nothing is appended optimistically, so the status pill never
 * disagrees with the thread under it — and the inbox is staled, because the ticket just
 * changed tab.
 */
function useTicketMutation<TInput>(
  ticketId: string,
  fn: (input: TInput) => ReturnType<typeof consoleApi.supportTicket>,
  feedback: MutationFeedback<SupportTicket, TInput>,
) {
  const client = useQueryClient();
  return useAdminMutation({
    fn,
    invalidate: [queryKeys.support.tickets()],
    ...feedback,
    onSuccess: (ticket, input) => {
      client.setQueryData(queryKeys.support.ticket(ticketId), ticket);
      feedback.onSuccess?.(ticket, input);
    },
  });
}

export const useReplyToTicketMutation = (
  ticketId: string,
  feedback: MutationFeedback<SupportTicket, string>,
) =>
  useTicketMutation(
    ticketId,
    (body: string) => consoleApi.replyToSupportTicket(ticketId, { body }),
    feedback,
  );

export const useCloseTicketMutation = (
  ticketId: string,
  feedback: MutationFeedback<SupportTicket, void>,
) => useTicketMutation(ticketId, () => consoleApi.closeSupportTicket(ticketId), feedback);

export const useUpdatePolicyMutation = () =>
  useAdminMutation({
    fn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      consoleApi.updatePolicy(key, enabled),
    invalidate: [queryKeys.policies()],
  });

export interface PlanPriceInput {
  plan: AdminPlan;
  /** The new price, in halalas. */
  priceMinor: number;
}

/**
 * One plan's price. Everything but the price goes back exactly as read, so the editor
 * can never rename or reorder a plan by accident. Every call shares one mutation key, so
 * the editor can ask whether any plan is still saving (`useIsSavingPlans`).
 */
export function useUpdatePlanMutation(feedback: MutationFeedback<AdminPlan, PlanPriceInput> = {}) {
  const client = useQueryClient();
  return useAdminMutation({
    mutationKey: queryKeys.planUpdates(),
    fn: ({ plan, priceMinor }: PlanPriceInput) =>
      consoleApi.updatePlan(plan.id, {
        nameAr: plan.nameAr,
        nameEn: plan.nameEn,
        priceMinor,
        durationDays: plan.durationDays,
        features: plan.features,
        active: plan.active,
        sortOrder: plan.sortOrder,
      }),
    invalidate: [queryKeys.plans()],
    ...feedback,
    onSuccess: (updated, input) => {
      // The answer goes into the list at once, so the editor shows the saved price
      // rather than the old one until the refetch lands.
      client.setQueryData<AdminPlan[]>(queryKeys.plans(), (plans) =>
        plans?.map((plan) => (plan.id === updated.id ? updated : plan)),
      );
      feedback.onSuccess?.(updated, input);
    },
  });
}

export const useIsSavingPlans = () => useIsMutating({ mutationKey: queryKeys.planUpdates() }) > 0;

export type AdminInvite = CreateAdminBody;

/** The account exists once this answers; the answer carries its one-time password. */
export const useInviteAdminMutation = (feedback: MutationFeedback<InvitedAdmin, AdminInvite>) =>
  useAdminMutation({
    fn: (input: AdminInvite) => consoleApi.inviteAdmin(input),
    invalidate: [queryKeys.admins()],
    ...feedback,
  });

/** Turns the signed-in admin's own two-factor on or off; costs the current password. */
export const useUpdateMfaMutation = (
  feedback: MutationFeedback<AdminIdentity, AdminMfaUpdateBody>,
) =>
  useAdminMutation({
    fn: ({ enabled, password }: AdminMfaUpdateBody) => adminApi.updateMfa(enabled, password),
    ...feedback,
  });

export interface ReportWindow {
  from: string;
  to: string;
}

/**
 * Downloads the window's report as the API's CSV. A mutation rather than a query: it is
 * a one-off action an admin takes, each one audited by the API (`report.exported`), and
 * nothing is cached. The file is handed to the browser before the screen's toast.
 */
export const useReportExportMutation = (feedback: MutationFeedback<Blob, ReportWindow>) =>
  useAdminMutation({
    fn: async ({ from, to }: ReportWindow) => {
      const result = await consoleApi.reportExportCsv(from, to);
      if (result.ok) saveFile(result.data, reportCsvFilename(from, to));
      return result;
    },
    ...feedback,
  });
